import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import {
  collection,
  collectionGroup,
  query,
  where,
  getFirestore,
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import firebaseApp from "../../firebase/credenciales";
import {
  FaUserTimes,
  FaCalendarTimes,
  FaUsers,
  FaChartBar,
} from "react-icons/fa";
import { format } from "date-fns";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

function TrabajadoresAusentesMes({ usuario }) {
  const [rucEmpresa, setRucEmpresa] = useState(usuario?.ruc || "");
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM")
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Datos
  const [ausencias, setAusencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);

  // Indicadores
  const [indicadores, setIndicadores] = useState({
    totalAusencias: 0,
    descansos: 0,
    permisos: 0,
    paternidad: 0,
    maternidad: 0,
    otros: 0,
    empleadosAfectados: 0,
    diasAusentes: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      establecerOrigenConsumo("ausencias:trabajadores");
      // Rango de fechas del mes
      const [year, month] = selectedMonth.split("-");
      const fechaInicio = new Date(year, month - 1, 1);
      const fechaFin = new Date(year, month, 0, 23, 59, 59);

      // Buscar empleados activos de la empresa
      const empleadosRef = collection(db, "users");
      const qEmpleados = query(
        empleadosRef,
        where("rucEmpresa", "==", rucEmpresa),
        where("activo", "==", true)
      );
      const snapEmpleados = await getDocs(qEmpleados);
      const empleadosArr = [];
      snapEmpleados.forEach((doc) => empleadosArr.push(doc.data()));

      // Buscar ausencias usando collectionGroup
      const ausenciasQuery = query(
        collectionGroup(db, "ausencias"),
        where("ruc", "==", rucEmpresa),
        where("fechaInicio", "<=", fechaFin)
      );
      const ausenciasSnapshot = await getDocs(ausenciasQuery);

      const ausenciasArr = [];
      const empleadosAfectadosSet = new Set();
      let descansos = 0,
        permisos = 0,
        paternidad = 0,
        maternidad = 0,
        otros = 0,
        diasAusentes = 0;

      ausenciasSnapshot.forEach((doc) => {
        const ausencia = doc.data();
        if (ausencia.fechaInicio && ausencia.fechaFin) {
          const ini = ausencia.fechaInicio.seconds
            ? new Date(ausencia.fechaInicio.seconds * 1000)
            : ausencia.fechaInicio.toDate
            ? ausencia.fechaInicio.toDate()
            : new Date(ausencia.fechaInicio);
          const fin = ausencia.fechaFin.seconds
            ? new Date(ausencia.fechaFin.seconds * 1000)
            : ausencia.fechaFin.toDate
            ? ausencia.fechaFin.toDate()
            : new Date(ausencia.fechaFin);

          // Verificar si hay solapamiento con el mes seleccionado
          if (ini <= fechaFin && fin >= fechaInicio) {
            ausenciasArr.push({
              ...ausencia,
              dni: ausencia.dni,
              nombreCompleto: ausencia.nombreCompleto || "",
            });

            empleadosAfectadosSet.add(ausencia.dni);

            // Clasificar tipo según los nuevos tipos
            const tipo = ausencia.tipo || "";
            if (
              tipo.includes("DescansoMedico") ||
              tipo.toLowerCase().includes("médico") ||
              tipo.toLowerCase().includes("enfermedad")
            ) {
              descansos++;
            } else if (
              tipo.includes("Permisos") ||
              tipo.toLowerCase().includes("permiso")
            ) {
              permisos++;
            } else if (
              tipo.includes("Paternidad") ||
              tipo.toLowerCase().includes("paternidad")
            ) {
              paternidad++;
            } else if (
              tipo.includes("Maternidad") ||
              tipo.toLowerCase().includes("maternidad")
            ) {
              maternidad++;
            } else {
              otros++;
            }

            // Calcular días de ausencia en el mes
            const desde = ini < fechaInicio ? fechaInicio : ini;
            const hasta = fin > fechaFin ? fechaFin : fin;
            const dias = Math.ceil((hasta - desde) / (1000 * 60 * 60 * 24)) + 1;
            diasAusentes += dias > 0 ? dias : 0;
          }
        }
      });

      setAusencias(ausenciasArr);
      setEmpleados(empleadosArr);

      setIndicadores({
        totalAusencias: ausenciasArr.length,
        descansos,
        permisos,
        paternidad,
        maternidad,
        otros,
        empleadosAfectados: empleadosAfectadosSet.size,
        diasAusentes,
      });
    } catch (err) {
      console.error("Error al cargar los datos:", err);
      setError("Error al cargar los datos.");
      setAusencias([]);
      setEmpleados([]);
      setIndicadores({
        totalAusencias: 0,
        descansos: 0,
        permisos: 0,
        paternidad: 0,
        maternidad: 0,
        otros: 0,
        empleadosAfectados: 0,
        diasAusentes: 0,
      });
    }
    setLoading(false);
  };

  function getBadgeColor(tipo) {
    const tipoLower = (tipo || "").toLowerCase();
    if (tipoLower.includes("vacacion")) return "primary";
    if (tipoLower.includes("médico") || tipoLower.includes("descanso"))
      return "danger";
    if (tipoLower.includes("permiso")) return "warning";
    if (tipoLower.includes("paternidad")) return "info";
    if (tipoLower.includes("maternidad")) return "success";
    return "secondary";
  }

  function exportarExcel() {
    // Construir datos para exportar
    const datos = empleados
      .filter((emp) => ausencias.some((a) => a.dni === emp.dni))
      .map((emp) => {
        const ausenciasEmp = ausencias.filter((a) => a.dni === emp.dni);
        return ausenciasEmp.map((a) => {
          // Calcular días de ausencia para esta ausencia específica
          let diasAusencia = 0;
          if (a.fechaInicio && a.fechaFin) {
            const ini = a.fechaInicio.seconds
              ? new Date(a.fechaInicio.seconds * 1000)
              : new Date(a.fechaInicio);
            const fin = a.fechaFin.seconds
              ? new Date(a.fechaFin.seconds * 1000)
              : new Date(a.fechaFin);

            // Obtener fechas límite del mes seleccionado
            const [year, month] = selectedMonth.split("-");
            const fechaInicio = new Date(year, month - 1, 1);
            const fechaFin = new Date(year, month, 0, 23, 59, 59);

            // Calcular días dentro del mes seleccionado
            const desde = ini < fechaInicio ? fechaInicio : ini;
            const hasta = fin > fechaFin ? fechaFin : fin;
            diasAusencia =
              Math.ceil((hasta - desde) / (1000 * 60 * 60 * 24)) + 1;
            diasAusencia = diasAusencia > 0 ? diasAusencia : 0;
          }

          return {
            DNI: emp.dni,
            Nombre: `${emp.name} ${emp.apellidos}`,
            Tipo: a.tipo,
            Estado: a.estado,
            Motivo: a.motivo,
            "Fecha Inicio": a.fechaInicio
              ? new Date(
                  a.fechaInicio.seconds
                    ? a.fechaInicio.seconds * 1000
                    : a.fechaInicio
                ).toLocaleDateString("es-PE")
              : "",
            "Fecha Fin": a.fechaFin
              ? new Date(
                  a.fechaFin.seconds ? a.fechaFin.seconds * 1000 : a.fechaFin
                ).toLocaleDateString("es-PE")
              : "",
            "Días de Ausencia": diasAusencia,
          };
        });
      })
      .flat();

    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ausentes");

    XLSX.writeFile(wb, `ausentes_${selectedMonth}.xlsx`);
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(45deg, #2c3e50, #3498db)",
              borderBottom: "3px solid #f39c12",
            }}
          >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaUsers className="me-3" />
                Trabajadores Ausentes del Mes
              </h2>
              <p className="mb-0 opacity-75">
                Listado de empleados con ausencias en el período seleccionado
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Formulario de filtros */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={6} lg={6}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label className="fw-semibold text-dark">
                        <FaCalendarTimes className="me-2" />
                        Período de Análisis
                      </Form.Label>
                      <Form.Control
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="form-control-lg"
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} lg={3}>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={fetchData}
                      disabled={loading}
                      className="w-100"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #3498db, #2980b9)",
                        border: "none",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Analizando datos...
                        </>
                      ) : (
                        <>
                          <FaChartBar className="me-2" />
                          Ver Ausentes
                        </>
                      )}
                    </Button>
                  </Col>
                  <Col md={3} lg={3}>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={exportarExcel}
                      disabled={loading || ausencias.length === 0}
                      className="w-100"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #27ae60, #16a085)",
                        border: "none",
                      }}
                    >
                      Exportar a Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Error */}
            {error && (
              <Alert
                variant="warning"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaUserTimes className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Error en la consulta</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                </div>
              </Alert>
            )}
            {/* Detalle por Empleado */}
            {!loading && empleados.length > 0 && (
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-success text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaUsers className="me-2" />
                    <h5 className="mb-0 fw-semibold">
                      Empleados con Ausencias
                    </h5>
                    <Badge bg="light" text="success" className="ms-auto">
                      {indicadores.empleadosAfectados} empleado(s) con ausencias
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-semibold border-0">DNI</th>
                          <th className="fw-semibold border-0">
                            Nombre Completo
                          </th>
                          <th className="fw-semibold border-0 text-center">
                            Ausencias
                          </th>
                          <th className="fw-semibold border-0 text-center">
                            Días Ausentes
                          </th>
                          <th className="fw-semibold border-0 text-center">
                            Estados
                          </th>
                          <th className="fw-semibold border-0 text-center">
                            Tipos de Ausencia
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {indicadores.empleadosAfectados === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              <FaUsers className="me-2" />
                              No hay empleados con ausencias en este período
                            </td>
                          </tr>
                        ) : (
                          empleados
                            .filter((emp) => {
                              // Solo mostrar empleados que tienen ausencias
                              const ausenciasEmp = ausencias.filter(
                                (a) => a.dni === emp.dni
                              );
                              return ausenciasEmp.length > 0;
                            })
                            .map((emp) => {
                              const ausenciasEmp = ausencias.filter(
                                (a) => a.dni === emp.dni
                              );
                              let diasAusEmp = 0;

                              // Calcular días de ausencia
                              ausenciasEmp.forEach((a) => {
                                if (a.fechaInicio && a.fechaFin) {
                                  const ini = a.fechaInicio.seconds
                                    ? new Date(a.fechaInicio.seconds * 1000)
                                    : new Date(a.fechaInicio);
                                  const fin = a.fechaFin.seconds
                                    ? new Date(a.fechaFin.seconds * 1000)
                                    : new Date(a.fechaFin);
                                  const [year, month] =
                                    selectedMonth.split("-");
                                  const fechaInicio = new Date(
                                    year,
                                    month - 1,
                                    1
                                  );
                                  const fechaFin = new Date(
                                    year,
                                    month,
                                    0,
                                    23,
                                    59,
                                    59
                                  );
                                  const desde =
                                    ini < fechaInicio ? fechaInicio : ini;
                                  const hasta = fin > fechaFin ? fechaFin : fin;
                                  const dias =
                                    Math.ceil(
                                      (hasta - desde) / (1000 * 60 * 60 * 24)
                                    ) + 1;
                                  diasAusEmp += dias > 0 ? dias : 0;
                                }
                              });

                              // Obtener tipos y estados únicos para este empleado
                              const tiposAusencias = [
                                ...new Set(ausenciasEmp.map((a) => a.tipo)),
                              ];
                              const estadosAusencias = [
                                ...new Set(ausenciasEmp.map((a) => a.estado)),
                              ];

                              // Función para obtener color del badge según el estado
                              const getEstadoBadgeColor = (estado) => {
                                const estadoLower = (
                                  estado || ""
                                ).toLowerCase();
                                if (estadoLower.includes("aprobado"))
                                  return "success";
                                if (estadoLower.includes("pendiente"))
                                  return "warning";
                                if (estadoLower.includes("rechazado"))
                                  return "danger";
                                return "secondary";
                              };

                              return (
                                <tr key={emp.dni}>
                                  <td className="py-3">
                                    <code className="bg-light px-2 py-1 rounded">
                                      {emp.dni}
                                    </code>
                                  </td>
                                  <td className="py-3">
                                    <div className="fw-semibold">
                                      {emp.name} {emp.apellidos}
                                    </div>
                                  </td>
                                  <td className="py-3 text-center">
                                    <Badge bg="danger" className="px-3 py-2">
                                      {ausenciasEmp.length}
                                    </Badge>
                                  </td>
                                  <td className="py-3 text-center">
                                    <Badge
                                      bg={
                                        diasAusEmp > 5
                                          ? "danger"
                                          : diasAusEmp > 2
                                          ? "warning"
                                          : "info"
                                      }
                                      className="px-3 py-2"
                                    >
                                      {diasAusEmp}
                                    </Badge>
                                  </td>
                                  <td className="py-3 text-center">
                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                      {estadosAusencias.map((estado, index) => (
                                        <Badge
                                          key={index}
                                          bg={getEstadoBadgeColor(estado)}
                                          className="small"
                                        >
                                          {estado}
                                        </Badge>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="py-3 text-center">
                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                      {tiposAusencias.map((tipo, index) => (
                                        <Badge
                                          key={index}
                                          bg={getBadgeColor(tipo)}
                                          className="small"
                                        >
                                          {tipo}
                                        </Badge>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default TrabajadoresAusentesMes;
